import { useState, useEffect, useCallback } from 'react'

interface AllowedDomain {
  domain: string
  note: string
}

export function useAllowedDomains() {
  const [domains, setDomains] = useState<AllowedDomain[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchDomains = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch('/api/allowed-domains')
      if (!response.ok) throw new Error('Failed to fetch allowed domains')
      const data = await response.json()
      setDomains(data.domains || [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchDomains()
  }, [fetchDomains])

  const addDomain = async (domain: string, note?: string) => {
    const res = await fetch('/api/allowed-domains', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ domain, note }),
    })
    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.error || 'Failed to add domain')
    }
    await fetchDomains()
  }

  const removeDomain = async (domain: string) => {
    const res = await fetch(`/api/allowed-domains/${encodeURIComponent(domain)}`, {
      method: 'DELETE',
    })
    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.error || 'Failed to remove domain')
    }
    await fetchDomains()
  }

  return { domains, loading, error, addDomain, removeDomain, refetch: fetchDomains }
}
